/**
 * One user, on their own terms.
 *
 * The list answers "who exists" and now handles the common edits itself. This
 * answers what a dialog cannot hold: every grant this person has, whether they
 * are signed in right now, and what each of those grants can be changed to.
 *
 * Every write here opens the same dialog the list uses. Not for tidiness - for
 * behaviour: one place decides how a failed save is reported, what a suspension
 * warns about, and that the screen re-reads the server afterwards rather than
 * trusting the form.
 */
import { ROLE_LABEL, ROLE_REACH, type TenantRoleName } from '../roles.ts';
import { useState } from 'react';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import type { AdminClinicList, AdminStore, AdminUserDetail, ListResponse } from '../api.ts';
import { del } from '../api.ts';
import { DangerButton } from '../components/DangerButton.tsx';
import { Card, Detail, Details, Empty, Pill, StatusPill, jaDate } from '../components/ui.tsx';
import { UserEditor } from '../components/UserEditor.tsx';
import { ImageUpload } from '../components/ImageUpload.tsx';
import { ModalForm } from '../components/Modal.tsx';


interface LoaderData {
  user: AdminUserDetail;
  clinics: AdminClinicList;
  stores: ListResponse<AdminStore>;
}

/** One registered value, or a dash. The same row the clinic pages use. */
function UserFact({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="factgrid__row">
      <dt>{label}</dt>
      <dd>{children || <span className="muted">—</span>}</dd>
      {hint && <dd className="muted small">{hint}</dd>}
    </div>
  );
}

export default function AdminUserDetailPage() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { user, clinics, stores } = useLoaderData<LoaderData>();

  /** Which dialog is open. Never two at once. */
  const [dialog, setDialog] = useState<{ kind: 'edit' | 'revokeAffiliation' } | null>(null);

  /*
   * The membership rows, read as the one thing they describe.
   *
   * The API still answers with a row per grant, because that is what the tables
   * hold. A person has one clinic, so the clinic is whichever the rows name; a
   * clinic-wide row makes them its admin, and the rest are their branches.
   */
  const active = user.memberships.filter((membership) => membership.status === 'ACTIVE');
  const role: TenantRoleName = active.some((membership) => membership.store_id === null)
    ? 'CLINIC_ADMIN'
    : active.some((membership) => membership.role === 'STORE_ADMIN')
      ? 'STORE_ADMIN'
      : 'STORE_USER';
  const affiliation = {
    clinicId: active[0]?.clinic_id ?? null,
    clinicName: active[0]?.clinic_name ?? null,
    clinicAdmin: active.some((membership) => membership.store_id === null),
    role,
    stores: active
      .filter((membership) => membership.store_id !== null)
      .map((membership) => ({ id: membership.store_id!, name: membership.store_name ?? '' })),
  };
  const close = () => setDialog(null);

  const claimed = user.issuer !== 'provisioned';

  return (
    <>
      {/*
        A profile, rather than a card with a name on it.

        The picture, the name and the two facts that decide what this account
        can do sit together at the top; everything else is a labelled row
        underneath. The old screen led with a title and then listed six rows of
        equal weight, so "is this person a JMC operator" and "when did they
        register" read as equally important - and one of those is an
        authorization question.
      */}
      <Card>
        <div className="profile">
          {/*
            The picture and the way to change it are the same thing.

            It was display-only, which left an operator with a face they could
            not replace and a Google photo they could not remove. Most of these
            accounts are provisioned - registered by JMC and never signed into
            with an account that carries a photo - so "the person can upload
            their own" is not an answer for whoever is setting the clinic up.
          */}
          <ImageUpload
            imageId={user.avatar_id}
            endpoint={`/admin/users/${user.id}/avatar`}
            label="画像を選ぶ"
            placeholder={(user.display_name || user.email || '?').trim().charAt(0).toUpperCase()}
            onDone={() => revalidator.revalidate()}
          />

          <div className="profile__identity">
            <h1 className="profile__name">
              {user.display_name || user.email || 'ユーザー'}
            </h1>
            <p className="profile__email">{user.email ?? 'メールアドレスなし'}</p>
            <div className="chips">
              {user.platform_role === 'JMC_ADMIN' ? (
                <Pill tone="danger">JMC管理者</Pill>
              ) : (
                <Pill>クリニック側ユーザー</Pill>
              )}
              <StatusPill status={user.status} />
              {user.has_password && <Pill tone="muted">パスワード設定済み</Pill>}
              {user.locked && <Pill tone="warn">ログイン試行の制限中</Pill>}
              {claimed ? (
                user.liveSessions > 0 ? (
                  <Pill tone="warn">ログイン中 {user.liveSessions} 件</Pill>
                ) : (
                  <Pill tone="muted">ログイン中のセッションなし</Pill>
                )
              ) : (
                <Pill tone="warn">未ログイン</Pill>
              )}
            </div>
          </div>

          <div className="profile__actions">
            <button className="btn btn--sm" type="button" onClick={() => setDialog({ kind: 'edit' })}>
              編集
            </button>
            <Link className="btn btn--sm btn--ghost" to="/admin/users">
              一覧に戻る
            </Link>
          </div>
        </div>

        <dl className="factgrid">
          <UserFact label="表示名">{user.display_name}</UserFact>
          <UserFact
            label="メールアドレス"
            hint={
              claimed
                ? 'ご本人のGoogleアカウントのアドレスです。変更できません。'
                : 'まだ一度もログインしていません。このアドレスでログインした人がこのアカウントを引き継ぎます。'
            }
          >
            {user.email}
          </UserFact>
          <UserFact label="アカウント状態">
            <StatusPill status={user.status} />
          </UserFact>
          <UserFact label="区分">
            {user.platform_role === 'JMC_ADMIN' ? 'JMC管理者' : 'クリニック側ユーザー'}
          </UserFact>
          <UserFact label="最終ログイン">{claimed ? jaDate(user.last_login_at) : null}</UserFact>
          <UserFact label="登録日">{jaDate(user.created_at)}</UserFact>
          <UserFact
            label="ログイン方法"
            hint="provisioned は、JMCが先に作成してまだ本人がログインしていないアカウントです。"
          >
            {user.issuer}
          </UserFact>
        </dl>
      </Card>

      {/*
        One affiliation, not a list of membership rows.

        A person belongs to one clinic and is either its admin or covers some of
        its branches (migration 074). Editing that as separate rows meant an
        operator wanting to promote somebody had to revoke a row and add another
        - two irreversible steps with a gap in the middle where the person could
        reach nothing.
      */}
      <Card
        title="所属"
        caption="所属できるクリニックは1つです。ここを変更すると、そのユーザーが見える範囲が変わり、ログイン中のセッションは無効になります。"
        actions={
          user.platform_role === null && (
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => setDialog({ kind: 'edit' })}
              disabled={user.email === null}
            >
              {affiliation.clinicId ? '所属を変更' : '＋ 所属を追加'}
            </button>
          )
        }
      >
        {affiliation.clinicId === null ? (
          <Empty>
            所属がありません。
            {user.platform_role === 'JMC_ADMIN'
              ? 'JMC管理者はクリニックに所属しません（記事本文の閲覧はサポート権限昇格が必要です）。'
              : '「所属を追加」から、クリニックの管理ユーザーか、店舗の担当者として登録できます。'}
          </Empty>
        ) : (
          <>
            <Details>
              <Detail label="クリニック">
                <Link to={`/admin/clinics/${affiliation.clinicId}`}>{affiliation.clinicName}</Link>
              </Detail>
              <Detail
                label="権限"
                hint={ROLE_REACH[affiliation.role]}
              >
                <Pill tone={affiliation.role === 'STORE_USER' ? 'muted' : 'default'}>
                  {ROLE_LABEL[affiliation.role]}
                </Pill>
              </Detail>
              <Detail label="担当店舗">
                {affiliation.clinicAdmin ? (
                  <span className="muted">全店舗</span>
                ) : affiliation.stores.length === 0 ? (
                  <Pill tone="warn">担当店舗なし</Pill>
                ) : (
                  <span className="dashcounts">
                    {affiliation.stores.map((store) => (
                      <Pill key={store.id} tone="muted">
                        {store.name}
                      </Pill>
                    ))}
                  </span>
                )}
              </Detail>
            </Details>

            <div className="chips" style={{ marginTop: 12 }}>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => setDialog({ kind: 'revokeAffiliation' })}
              >
                所属を解除
              </button>
            </div>
          </>
        )}
      </Card>

      {/*
        Deletion, and the reason it sometimes refuses.
        Eight authorship columns are ON DELETE RESTRICT: an ACTIVE prompt version
        must always name who approved it. So a user who has approved anything
        cannot be erased, and the server says so rather than pretending. The
        answer in that case is 状態→停止 in 編集 above, which ends every session
        and blocks sign-in while leaving the attribution readable.
      */}
      <Card title="アカウントの削除" caption="取り消せません。承認記録があるユーザーは削除できません。">
        <DangerButton
          label="このアカウントを削除する"
          confirmText={user.email ?? user.display_name}
          consequences={[
            'このアカウントのログイン情報とセッション',
            `クリニック・店舗への所属 ${user.memberships.length} 件`,
          ]}
          onConfirm={() => del(`/admin/users/${user.id}`)}
          onDone={() => void navigate('/admin/users')}
        />
        <p className="muted small" style={{ marginBottom: 0 }}>
          プロンプトの承認やコーパスの有効化を行ったアカウントは削除できません。
          その場合は上の「編集」から状態を<strong>停止</strong>にしてください。
          ログインとセッションは即時に無効になり、「誰が承認したか」の記録は残ります。
        </p>
      </Card>

      {dialog?.kind === 'edit' && (
        <UserEditor
          user={user}
          affiliation={affiliation}
          clinics={clinics.items}
          stores={stores.items}
          open
          onClose={close}
        />
      )}
      {dialog?.kind === 'revokeAffiliation' && affiliation.clinicId && (
        <ModalForm
          open
          onClose={close}
          title="所属を解除しますか"
          caption={`${affiliation.clinicName}：解除すると、このクリニックの記事は開けなくなります。アカウント自体は残ります。`}
          submitLabel="解除する"
          danger
          onSubmit={() => del(`/admin/clinics/${affiliation.clinicId!}/members/${user.id}`)}
        >
          <p className="field--full" style={{ margin: 0 }}>
            ログイン中のセッションは直ちに終了します。別のクリニックへ移す場合は、解除したうえで
            「編集」から登録してください。
          </p>
        </ModalForm>
      )}
    </>
  );
}
